import axios from "axios";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { saveAs } from "@progress/kendo-file-saver";
import Key from "../../clientVariables.json";
import { toast } from "react-toastify";
import { flattenOptimizedSkills } from "./resumeDataUtils";
import { openAI } from "../common/OpenAIComponent";

const ALLOWED_ADMIN_COMPANIES = [4854, 45721, 483370, 446766];

const dataURLtoFile = (dataUrl, filename) => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const buildProjectsPayload = (projectdetails = []) =>
  projectdetails.map((project) => ({
    id: project.id,
    projectTitle: project.pro_title,
    country: project.pro_country,
    countryname: project.countryName,
    state: project.pro_state,
    statename: project.statename,
    city: project.pro_city,
    cityname: project.cityname,
    title: project.pro_role,
    responsibilities: project.pro_responsibilities,
    description: project.pro_description,
    startdate: project.pro_startdate,
    enddate: project.pro_enddate,
    role: project.pro_role,
    company: project.pro_client,
    checked: project.is_current_project === 1,
    is_current_project: project.is_current_project,
    pro_client_domain: project.pro_client_domain,
    pro_technologies: project.pro_technologies,
    pro_address_full: project.pro_address_full,
    pro_zipcode: project.pro_zipcode,
    pro_till_date: project.pro_till_date,
    pro_createdate: project.pro_createdate,
    pro_modified: project.pro_modified,
    pro_createdtime: project.pro_createdtime,
    pro_country_new: project.pro_country_new,
    pro_state_new: project.pro_state_new,
    pro_city_new: project.pro_city_new,
  }));

export const buildEducationsPayload = (education = []) =>
  (education || []).map((item) => ({
    edu_course: item.degree || item.edu_course || "",
    edu_university: item.university || item.edu_university || "",
    edu_passing_year: item.yearofpassing || item.edu_passing_year || "",
  }));

export const buildCertificatesPayload = (certificates = []) =>
  (certificates || []).map((item) => {
    if (typeof item === "string") return item;
    return item.certName || "";
  }).filter(Boolean);

export const buildAdvancedResumePayload = ({
  optimizedData,
  resumeFullDetails,
  contextObject,
  user,
  selectedCandidate,
  relatedTitles,
}) => {
  const resumedetails = resumeFullDetails?.resumedetails || {};
  const isAdminCompany = ALLOWED_ADMIN_COMPANIES.includes(Number(user?.companyid));
  const candidateId = isAdminCompany
    ? selectedCandidate?.candidate_id || selectedCandidate?.id
    : user?.candidate_id;

  const allSkills = flattenOptimizedSkills(optimizedData);
  const skillsList = allSkills.length > 0 ? allSkills : [];

  return {
    candidate_id: candidateId,
    ai_skills: skillsList,
    user_id: user?.id,
    application_title: contextObject?.jobTitle || resumedetails.application_title || "",
    full_name: resumedetails.full_name,
    first_name: resumedetails.first_name,
    last_name: resumedetails.last_name,
    home_phone: resumedetails.home_phone || resumedetails.home_phone,
    email_address: resumedetails.email,
    total_experience: contextObject?.experience || resumedetails.total_experience || "",
    location_state: resumedetails.location_state || contextObject?.location_state || "",
    full_address: resumedetails.address_full || "",
    location_city: resumedetails.address_city || contextObject?.location_city || "",
    summary: contextObject?.summary || optimizedData?.summary || "",
    skills_titles: skillsList,
    source: "fromBenchsales",
    uploaded_uid: user?.id,
    linkedin_url: resumedetails.linkedin_url || "",
    filename: resumedetails.filename,
    projects_data: buildProjectsPayload(resumeFullDetails?.projectdetails || []),
    educations_data: buildEducationsPayload(optimizedData?.education || []),
    certificates_data: buildCertificatesPayload(optimizedData?.certificate || []),
    solr_search_string: relatedTitles
  };
};

export const saveAdvancedResume = async ({
  containerElement,
  optimizedData,
  resumeFullDetails,
  contextObject,
  user,
  selectedCandidate,
  navigate,
  onExportStart,
  onExportEnd,
  selectedJob,
}) => {

  if (!containerElement) {
    throw new Error("Resume preview not ready.");
  }

  if (!user) {
    throw new Error("User session not found.");
  }

  const isAdminCompany = ALLOWED_ADMIN_COMPANIES.includes(Number(user?.companyid));
  const candidateId = isAdminCompany
    ? selectedCandidate?.candidate_id || selectedCandidate?.id
    : user?.candidate_id;

  if (!candidateId) {
    throw new Error("Candidate ID not found.");
  }

  onExportStart?.();

  try {
    await new Promise((resolve) => setTimeout(resolve, 100));

    const group = await drawDOM(containerElement, {
      paperSize: "A4",
      margin: 38,
      scale: 0.8,
    });

    const dataUri = await exportPDF(group);
    const resumedetails = resumeFullDetails?.resumedetails || {};
    const fileName = `${resumedetails?.full_name || "resume"}.pdf`;
    const pdfFile = dataURLtoFile(dataUri, fileName);

    const getTitleSuggestions = () => {
      return new Promise((resolve) => {
        const title =
          selectedJob?.jobtitle &&
            selectedJob.jobtitle !== "null" &&
            selectedJob.jobtitle !== "undefined"
            ? selectedJob.jobtitle
            : contextObject?.jobTitle;

        if (!title) {
          console.warn("Please Enter Title");
          resolve([]);
          return;
        }

        const msg = `Extract exactly 10 related job roles for the following job title: "${title}".
Rules:
- Return ONLY a comma-separated list of job role titles inside the JSON
- Each role must be 2+ words (no single-word roles)
- Do NOT include skills, technologies, tools, or frameworks (e.g. no "Java", "AWS", "Python")
- Do NOT repeat the same role in different forms
- Only include roles closely related to the job title's industry and responsibilities
- Maximum 10 roles, no more

Return ONLY valid JSON: { "related_titles": ["Role 1", "Role 2", ...] }`;

        openAI(msg, (error, results) => {
          if (error) {
            console.error("Error fetching titles:", error);
            resolve([]);
          } else {
            const titles = results?.data?.related_titles || results?.related_titles || [];
            resolve(titles);
          }
        });
      });
    };

    const relatedTitles = await getTitleSuggestions();

    if (!relatedTitles.length) {
      toast.error("Related titles not found. Please try again.");
      return;
    }

    const resumePayload = buildAdvancedResumePayload({
      optimizedData,
      resumeFullDetails,
      contextObject,
      user,
      selectedCandidate,
      relatedTitles,
    });

    const resumeResponse = await axios.post(
      `${Key.domain}/candidate/bench_addFullResume`,
      resumePayload
    );

    const resumeId = resumeResponse?.data?.data?.insertId;
    if (!resumeId) {
      throw new Error("Failed to save resume details.");
    }

    saveAs(dataUri, `resume_${resumedetails?.full_name || "resume"}`);

    const uploadData = new FormData();
    uploadData.append("file", pdfFile);
    uploadData.append("userid", user?.id);
    uploadData.append("candidateid", candidateId);
    uploadData.append("filename", pdfFile.name);
    uploadData.append("file_size", pdfFile.size);
    uploadData.append("file_type", pdfFile.type);
    uploadData.append("attachment_type", "Candidate_res_attachment");
    uploadData.append("attachment_name", pdfFile.name);
    uploadData.append("file_created", pdfFile.lastModifiedDate);
    uploadData.append("resumeid", resumeId);
    uploadData.append(
      "role",
      contextObject?.jobTitle || resumedetails.application_title || ""
    );

    await axios.post(`${Key.domain}/candidate/upload_document`, uploadData);

    toast.success("Resume saved successfully!");

    if (isAdminCompany) {
      contextObject?.updateFinish?.(false);
      contextObject?.updateJobTitle?.("");
      contextObject?.updateEmail?.("");
      contextObject?.updatePhone?.("");
      contextObject?.updateExperience?.("");
      contextObject?.updateFinishOne?.(false);
      contextObject?.updateSummary?.("");
      contextObject?.updateAISkills?.([]);
      navigate("/my-candidates");
    } else {
      contextObject?.updateFinish?.(true);
      navigate("/candidates-list");
    }

    return resumeId;
  } finally {
    onExportEnd?.();
  }
};
